import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Location } from '@freelancer/location';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { IconColor } from '@freelancer/ui/icon';
import { TextSize } from '@freelancer/ui/text';
import { ViewHeaderTemplate } from '@freelancer/view-header-template';
import { map } from 'rxjs/operators';

enum ChildNavLink {
  MESSAGES = '/messages',
  UPDATES = '/navigation/updates',
}

@Component({
  selector: 'app-navigation-child',
  template: `
    <ng-container *ngIf="!loading">
      <ng-container
        *ngIf="template && fullReplacement; else defaultView"
        [ngTemplateOutlet]="template"
      ></ng-container>

      <ng-template #defaultView>
        <fl-view-header [showBackButton]="!primary">
          <fl-bit class="NavigationChild" flTrackingSection="NavigationChild">
            <fl-bit class="NavigationChild-title">
              <ng-container *ngIf="template && !fullReplacement">
                <fl-heading
                  *ngIf="primary"
                  [color]="HeadingColor.INHERIT"
                  [headingType]="HeadingType.H1"
                  [size]="TextSize.LARGE"
                  [truncate]="true"
                >
                  <ng-container [ngTemplateOutlet]="template"></ng-container>
                </fl-heading>
                <fl-heading
                  *ngIf="!primary"
                  [color]="HeadingColor.INHERIT"
                  [headingType]="HeadingType.H1"
                  [size]="TextSize.MID"
                  [truncate]="true"
                >
                  <ng-container [ngTemplateOutlet]="template"></ng-container>
                </fl-heading>
              </ng-container>
            </fl-bit>

            <fl-bit class="NavigationChild-actions">
              <fl-button
                class="NavigationChild-action"
                flTrackingLabel="Updates"
                [ngClass]="{
                  IsActive: currentUrl === ChildNavLink.UPDATES
                }"
                [link]="ChildNavLink.UPDATES"
                (click)="handleUpdatesClick()"
              >
                <fl-bit class="IndicatorContainer">
                  <fl-icon
                    [color]="IconColor.INHERIT"
                    [name]="'ui-bell-outline-v2'"
                  ></fl-icon>
                  <fl-unread-indicator
                    *ngIf="unreadUpdates"
                    ariaLabel="Unread updates"
                    i18n-ariaLabel="Unread updates indicator label"
                  ></fl-unread-indicator>
                </fl-bit>
              </fl-button>

              <fl-button
                class="NavigationChild-action"
                flTrackingLabel="Messages"
                [ngClass]="{
                  IsActive: currentUrl === ChildNavLink.MESSAGES
                }"
                [link]="ChildNavLink.MESSAGES"
              >
                <fl-bit class="IndicatorContainer">
                  <fl-icon
                    [color]="IconColor.INHERIT"
                    [name]="'ui-chat-square-outline'"
                  ></fl-icon>
                  <fl-unread-indicator
                    *ngIf="hasUnreadMessages"
                    ariaLabel="Unread messages"
                    i18n-ariaLabel="Unread messages indicator label"
                  ></fl-unread-indicator>
                </fl-bit>
              </fl-button>
            </fl-bit>
          </fl-bit>
        </fl-view-header>
      </ng-template>
    </ng-container>
  `,
  styleUrls: ['./navigation-child.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationChildComponent implements OnChanges, OnInit {
  ChildNavLink = ChildNavLink;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  IconColor = IconColor;
  TextSize = TextSize;

  currentUrl: string;
  fullReplacement?: boolean;
  template?: TemplateRef<any>;
  loading: boolean;
  unreadUpdates = false;

  @Input() hasUnreadMessages = false;
  @Input() hasUnreadUpdates = false;
  @Input() primary = true;

  constructor(
    private viewHeaderTemplate: ViewHeaderTemplate,
    private changeDetectorRef: ChangeDetectorRef,
    private location: Location,
  ) {}

  ngOnInit() {
    // using `| async` causes ChangedAfterItHasBeenChecked errors
    // we do a manual subscription to get around this instead
    this.viewHeaderTemplate.templateChanges().subscribe(newTemplateConfig => {
      this.template = newTemplateConfig?.templateRef;
      this.fullReplacement = newTemplateConfig?.fullReplacement;
      this.changeDetectorRef.detectChanges();
    });

    this.location
      .valueChanges()
      .pipe(map(location => location.pathname))
      .subscribe(pathname => {
        this.currentUrl = pathname;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('hasUnreadUpdates' in changes) {
      this.unreadUpdates = this.hasUnreadUpdates;
    }
  }

  handleUpdatesClick() {
    this.unreadUpdates = false;
  }
}
